import React, { useEffect, useState } from 'react';
import { Calendar, Loader2, Link2, LogOut, AlertTriangle, X, RefreshCcw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../utils/api';
import { FEATURE_CALENDAR_INTEGRATION } from '../utils/featureFlags';

/**
 * Gate de onboarding: si el usuario aún no conectó su Google Calendar
 * y la integración está configurada en el servidor, muestra un modal bloqueante
 * a pantalla completa antes de poder usar el ERP.
 */
const RequireCalendarSetup = ({ children }) => {
  // 🔒 Feature flag: si la integración está pausada, no bloqueamos el acceso.
  // (Ver /app/frontend/src/utils/featureFlags.js para detalles).
  if (!FEATURE_CALENDAR_INTEGRATION) {
    return <>{children}</>;
  }

  return <RequireCalendarSetupInner>{children}</RequireCalendarSetupInner>;
};

const RequireCalendarSetupInner = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [dismissedReconnect, setDismissedReconnect] = useState(() => {
    try { return sessionStorage.getItem('gcal_reconnect_dismissed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await api.get('/oauth/calendar/status');
        if (!cancelled) setStatus(r.data);
      } catch {
        if (!cancelled) setStatus({ configured: false, connected: false });
      }
    };
    fetchStatus();

    // Detectar retorno de OAuth para refrescar el estado y mostrar errores
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('google_calendar');
    if (flag === 'error') {
      const msg = params.get('msg') || '';
      const email = params.get('email') || '';
      if (msg === 'email_no_corporativo') {
        setErrorMsg(`La cuenta "${email}" no pertenece a @peritum.cl. Cierra sesión en Google y vuelve a conectar con tu cuenta corporativa.`);
      } else {
        setErrorMsg(`Error al conectar: ${msg}`);
      }
      // Limpiar URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (flag === 'ok') {
      setTimeout(fetchStatus, 500);
      window.history.replaceState({}, '', window.location.pathname);
    }
    return () => { cancelled = true; };
  }, []);

  const conectar = async () => {
    try {
      const r = await api.get('/oauth/calendar/start');
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(formatErrorMessage(e) || 'Error iniciando OAuth');
    }
  };

  const cerrarSesion = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const dismissReconnect = () => {
    setDismissedReconnect(true);
    try { sessionStorage.setItem('gcal_reconnect_dismissed', '1'); } catch { /* ignore */ }
  };

  // Aún cargando o no aplica el gate
  if (!status) return null;
  // Si la integración no está configurada por el admin, no bloqueamos (no tendría sentido)
  if (!status.configured) return <>{children}</>;
  // Ya conectado: pasar, pero opcionalmente mostrar banner de re-conexión si scope incompleto
  if (status.connected) {
    const showReconnectBanner = status.needs_reconnect && !dismissedReconnect;
    const missing = [];
    if (!status.has_freebusy_scope) missing.push('calendar.readonly');
    if (!status.has_drive_scope) missing.push('drive.readonly');
    const missingLabel = missing.join(' + ');
    const motivos = [];
    if (!status.has_freebusy_scope) motivos.push('verificar disponibilidad nativa (FreeBusy)');
    if (!status.has_drive_scope) motivos.push('importar automáticamente las transcripciones de Google Meet');
    return (
      <>
        {showReconnectBanner && (
          <div
            className="sticky top-0 z-40 bg-gradient-to-r from-amber-500 via-amber-400 to-orange-400 text-amber-950 shadow-md"
            data-testid="gcal-reconnect-banner"
          >
            <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <strong className="font-bold">Reconecta tu Google Calendar.</strong>{' '}
                <span className="opacity-90">
                  Tu conexión actual no incluye {missing.length > 1 ? 'los permisos' : 'el permiso'}{' '}
                  <code className="bg-amber-200/60 px-1 rounded text-[11px]">{missingLabel}</code>
                  {' '}necesario{missing.length > 1 ? 's' : ''} para {motivos.join(' y ')}.
                </span>
              </div>
              <Button
                size="sm"
                onClick={conectar}
                className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-8 px-3"
                data-testid="gcal-reconnect-btn"
              >
                <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Reconectar ahora
              </Button>
              <button
                type="button"
                onClick={dismissReconnect}
                aria-label="Cerrar aviso"
                className="p-1 hover:bg-amber-600/30 rounded transition-colors"
                data-testid="gcal-reconnect-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        {children}
      </>
    );
  }

  // Modal bloqueante
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cal-setup-gate">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-br from-rose-600 via-pink-600 to-purple-600 text-white p-6 text-center">
          <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-3">
            <Calendar className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold mb-1">Bienvenido a Peritum ERP</h2>
          <p className="text-sm opacity-90">Conecta tu Google Calendar para continuar</p>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700 text-center">
            Como parte del flujo del estudio, necesitamos sincronizar tu agenda corporativa
            con el ERP para coordinar reuniones, audiencias y compromisos con clientes.
          </p>

          <ul className="text-xs text-slate-600 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-bold mt-0.5">✓</span>
              <span>Visualiza tus eventos sin salir del ERP</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-bold mt-0.5">✓</span>
              <span>Agenda reuniones con leads y socios en un click</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-bold mt-0.5">✓</span>
              <span>Recibe el Valor del Día Peritum cada mañana</span>
            </li>
          </ul>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-900">
            <strong>Importante:</strong> usa tu cuenta corporativa <code>@peritum.cl</code>.
            Solo solicitamos permisos sobre tu calendario primario.
          </div>

          {errorMsg && (
            <div className="bg-rose-50 border-2 border-rose-300 rounded-lg p-3 text-xs text-rose-900" data-testid="cal-setup-error">
              <p className="font-bold mb-1">⛔ Conexión rechazada</p>
              <p>{errorMsg}</p>
            </div>
          )}

          <Button
            onClick={conectar}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="cal-setup-connect"
          >
            <Link2 className="w-4 h-4 mr-2" /> Conectar con Google Calendar
          </Button>

          <button
            type="button"
            onClick={cerrarSesion}
            className="w-full text-xs text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1.5 mt-2"
            data-testid="cal-setup-logout"
          >
            <LogOut className="w-3 h-3" /> Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequireCalendarSetup;
